import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { EligibilityLimitOverrideDtoIn } from '@/models/in/EligibilityLimitOverrideDtoIn';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';

const QUERY_URL = 'management/eligibility-limit-overrides';

interface DeleteOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  override_: EligibilityLimitOverrideDtoIn;
  onSuccess: () => void;
}

const DeleteOverrideDialog = ({
  open,
  onClose,
  override_,
  onSuccess,
}: DeleteOverrideDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: deleteOverride, isPending: isDeleting } = useDeleteAuthApi<void>(
    `${QUERY_URL}/${override_.id}`
  );

  const handleDelete = () => {
    setError(null);
    deleteOverride(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('Error deleting eligibility limit override:', err);
        setError(t('eligibilityOverrides.dialog.errorDeleting'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const name = override_.contactName ?? override_.fundName ?? '';
  const message = error
    ? `${t('eligibilityOverrides.dialog.confirmDelete', { name })}\n\n${error}`
    : t('eligibilityOverrides.dialog.confirmDelete', { name });

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('eligibilityOverrides.delete')}
      message={message}
      confirmText={isDeleting ? 'saving' : 'eligibilityOverrides.dialog.deleteConfirm'}
      cancelText="eligibilityOverrides.dialog.deleteCancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteOverrideDialog;
